import { useState, useEffect, useMemo } from "react";
import { Bell, MessageSquare, Eye, Check, X, AtSign, CheckSquare, Mail, FileText, UserPlus, Share2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { errorToast } from "@/components/ui/sonner";

// Include library_mention and library_reply in comment types
const COMMENT_TYPES = ['comment', 'mention', 'reply', 'library_mention', 'library_reply'];

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  read: boolean;
  created_at: string;
  project_id: string | null;
  actor_id: string | null;
  project_name?: string;
  actor_name?: string;
}

interface WatchedProject {
  id: string;
  project_id: string;
  project_name: string;
  project_stage: string | null;
  watch_comments: boolean;
  watch_stages: boolean;
}

type NotificationTab = 'all' | 'comments' | 'watching';

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [watchedProjects, setWatchedProjects] = useState<WatchedProject[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>('all');

  // Filter notifications by type
  const commentNotifications = useMemo(() => 
    notifications.filter(n => COMMENT_TYPES.includes(n.type)),
    [notifications]
  );

  // Count unread by category
  const unreadCounts = useMemo(() => ({
    all: notifications.filter(n => !n.read).length,
    comments: commentNotifications.filter(n => !n.read).length,
  }), [notifications, commentNotifications]);

  const fetchNotifications = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error fetching notifications:", error);
      return;
    }

    // Fetch project names for notifications with project_id
    const projectIds = [...new Set(data.filter(n => n.project_id).map(n => n.project_id))];
    let projectMap = new Map<string, string>();
    
    if (projectIds.length > 0) {
      const { data: projects } = await supabase
        .from("projects")
        .select("id, name")
        .in("id", projectIds as string[]);
      
      if (projects) {
        projectMap = new Map(projects.map(p => [p.id, p.name]));
      }
    }

    // Fetch actor names
    const actorIds = [...new Set(data.filter(n => n.actor_id).map(n => n.actor_id))];
    let actorMap = new Map<string, string>();
    
    if (actorIds.length > 0) {
      const { data: actors } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", actorIds as string[]);
      
      if (actors) {
        actorMap = new Map(actors.map(a => [a.id, a.name || a.email.split('@')[0]]));
      }
    }

    setNotifications(data.map(n => ({
      ...n,
      project_name: n.project_id ? projectMap.get(n.project_id) : undefined,
      actor_name: n.actor_id ? actorMap.get(n.actor_id) : undefined,
    })));
  };

  const fetchWatchedProjects = async () => {
    if (!user?.id) return;

    const { data: watchers, error } = await supabase
      .from("project_watchers")
      .select("id, project_id, watch_comments, watch_stages")
      .eq("user_id", user.id);

    if (error) {
      console.error("Error fetching watched projects:", error);
      return;
    }

    if (watchers && watchers.length > 0) {
      const projectIds = watchers.map((w) => w.project_id);
      
      const { data: projects } = await supabase
        .from("projects")
        .select("id, name, stage")
        .in("id", projectIds);
      
      const projectData: Record<string, { name: string; stage: string | null }> = {};
      if (projects) {
        projects.forEach(p => { projectData[p.id] = { name: p.name, stage: p.stage }; });
      }

      setWatchedProjects(
        watchers.map((w) => ({
          id: w.id,
          project_id: w.project_id,
          project_name: projectData[w.project_id]?.name || 'Unknown Proposal',
          project_stage: projectData[w.project_id]?.stage || null,
          watch_comments: w.watch_comments ?? true,
          watch_stages: w.watch_stages ?? true,
        }))
      );
    } else {
      setWatchedProjects([]);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      fetchWatchedProjects();
    }
  }, [user?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchWatchedProjects();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_watchers',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchWatchedProjects();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const markAsRead = async (id: string) => {
    const previous = notifications;
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", id);

    if (error) {
      setNotifications(previous);
      errorToast('Failed to mark notification as read. Please try again.');
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;

    const previous = notifications;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("read", false);

    if (error) {
      setNotifications(previous);
      errorToast('Failed to mark notifications as read. Please try again.');
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    if (notification.link) {
      setIsOpen(false);
      // For comment-type notifications, open the notes tab in the intelligence panel
      const isCommentType = COMMENT_TYPES.includes(notification.type) || notification.type === 'resolved';
      if (isCommentType && !notification.link.includes('tab=')) {
        const separator = notification.link.includes('?') ? '&' : '?';
        navigate(`${notification.link}${separator}tab=notes`);
      } else {
        navigate(notification.link);
      }
    }
  };

  const handleWatchedProjectClick = (projectId: string) => {
    setIsOpen(false);
    navigate(`/projects/${projectId}`);
  };

  const handleUnwatch = async (e: React.MouseEvent, watcherId: string) => {
    e.stopPropagation();
    
    // Optimistic update
    setWatchedProjects(prev => prev.filter(w => w.id !== watcherId));
    
    const { error } = await supabase
      .from("project_watchers")
      .delete()
      .eq("id", watcherId);
    
    if (error) {
      fetchWatchedProjects();
      errorToast('Failed to unwatch proposal');
    }
  };

  const formatStage = (stage: string | null) => {
    if (!stage) return 'No stage';
    return stage.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getActionText = (type: string) => {
    switch (type) {
      case 'comment':
        return 'added a comment';
      case 'mention':
      case 'library_mention':
      case 'comment_mention':
        return 'mentioned you in';
      case 'reply':
      case 'library_reply':
        return 'replied to you in';
      case 'assignment':
        return 'assigned you a task';
      case 'message':
        return 'sent you a message';
      case 'invitation':
        return 'invited you to';
      case 'share':
        return 'shared';
      case 'resolved':
        return 'resolved your note in';
      default:
        console.warn('Unknown notification type:', type);
        return 'updated';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'assignment':
        return CheckSquare;
      case 'message':
        return Mail;
      case 'comment':
      case 'library_reply':
        return MessageSquare;
      case 'mention':
      case 'library_mention':
      case 'comment_mention':
        return AtSign;
      case 'reply':
        return MessageSquare;
      case 'invitation':
        return UserPlus;
      case 'share':
        return Share2;
      case 'resolved':
        return CheckCircle2;
      default:
        return FileText;
    }
  };

  const formatTimeAgo = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const renderNotificationItem = (notification: Notification) => {
    const Icon = getNotificationIcon(notification.type);
    const isMention = notification.type === 'mention' || notification.type === 'library_mention' || notification.type === 'comment_mention';

    // Extract section name from title
    const sectionMatch = notification.title?.match(/in\s+(.+)$/);
    const sectionName = sectionMatch ? sectionMatch[1] : null;
    const showSection = notification.project_name && sectionName && sectionName !== notification.project_name;
    const isUnread = !notification.read;
    
    return (
      <button
        onClick={() => handleNotificationClick(notification)}
        className={cn(
          "w-full text-left px-4 py-3 flex gap-3 hover:bg-muted/40 transition-colors group relative",
          isUnread ? "bg-primary/5 border-l-2 border-l-primary" : "border-l-2 border-l-transparent"
        )}
      >

        {/* Icon chip or avatar */}
        {isMention && notification.actor_name ? (
          <Avatar className="shrink-0 w-8 h-8 mt-px">
            <AvatarFallback className="bg-muted text-muted-foreground text-[10px] font-medium">
              {getInitials(notification.actor_name)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className={cn(
            "shrink-0 flex items-center justify-center w-8 h-8 rounded-full mt-px",
            (notification.type === 'resolved' || notification.type === 'assignment') 
              ? "bg-success/15" 
              : isUnread ? "bg-primary/15" : "bg-muted/50"
          )}>
            <Icon className={cn(
              "w-4 h-4",
              (notification.type === 'resolved' || notification.type === 'assignment')
                ? "text-success"
                : isUnread ? "text-primary" : "text-muted-foreground/50"
            )} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          {/* Action line: who did what */}
          <p className="text-xs leading-snug">
            {notification.actor_name && (
              <span className={cn("font-semibold", isUnread ? "text-foreground" : "text-foreground/70")}>
                {notification.actor_name}
              </span>
            )}
            <span className="text-muted-foreground/70"> {getActionText(notification.type)}</span>
          </p>

          {/* Context row: Project › Section */}
          {(notification.project_name || notification.title) && (
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground/60 leading-none">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="font-medium truncate">
                {notification.project_name || notification.title}
              </span>
              {showSection && (
                <>
                  <span className="text-muted-foreground/30">›</span>
                  <span className="truncate">{sectionName}</span>
                </>
              )}
            </div>
          )}

          {/* Comment / message content */}
          {notification.message && (
            <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-1 border-l-2 border-l-border pl-2 mt-1">
              "{notification.message.replace(/@\[([^\]]+)\]/g, '@$1')}"
            </p>
          )}

          {/* Timestamp */}
          <p className="text-[10px] text-muted-foreground/60 pt-0.5">
            {formatTimeAgo(notification.created_at)}
          </p>
        </div>

        {/* Unread dot */}
        {isUnread && (
          <div className="shrink-0 self-start mt-1.5">
            <div className="size-1.5 rounded-full bg-primary" />
            <span className="sr-only">Unread</span>
          </div>
        )}
      </button>
    );
  };

  const renderNotificationList = (items: Notification[]) => {
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
          <Bell className="w-4 h-4 text-muted-foreground/30 mb-2" />
          <p className="text-[11px] text-muted-foreground/50">No notifications</p>
        </div>
      );
    }

    return (
      <div>
        {items.map((notification, idx) => (
          <div key={notification.id} className={cn(idx < items.length - 1 && "border-b border-border/30")}>
            {renderNotificationItem(notification)}
          </div>
        ))}
      </div>
    );
  };

  const renderWatchlist = () => {
    if (watchedProjects.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
          <Eye className="w-4 h-4 text-muted-foreground/30 mb-2" />
          <p className="text-[11px] text-muted-foreground/50">No watched proposals</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-border/20">
        {watchedProjects.map((project) => (
          <div
            key={project.id}
            onClick={() => handleWatchedProjectClick(project.project_id)}
            className="flex items-center gap-3 px-4 py-3 cursor-pointer group hover:bg-muted/40 transition-colors"
          >
            <div className="shrink-0 flex items-center justify-center size-7 rounded-md bg-muted/50">
              <Eye className="w-3.5 h-3.5 text-muted-foreground/50" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">
                {project.project_name}
              </p>
              <p className="text-[10px] text-muted-foreground/40 mt-0.5">
                {formatStage(project.project_stage)}
              </p>
            </div>
            
            <button
              className="h-6 w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive rounded"
              onClick={(e) => handleUnwatch(e, project.id)}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            className="relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <Bell className="w-[18px] h-[18px]" />
            <AnimatePresence>
              {unreadCounts.all > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none"
                >
                  {unreadCounts.all > 99 ? "99+" : unreadCounts.all}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className="min-w-[420px] w-[480px] p-0 border border-border rounded-xl overflow-hidden z-[1000] bg-card shadow-lg" 
          side="right"
          align="end"
          sideOffset={12}
          collisionPadding={16}
        >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-11 border-b border-border/30">
          <h3 className="text-[13px] font-semibold text-foreground">Notifications</h3>
          <button 
            className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
            onClick={markAllAsRead}
          >
            Mark all as read
          </button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as NotificationTab)} className="w-full">
          <div className="px-4 py-2 border-b border-border/20">
            <div className="flex items-center gap-1">
              {[
                { value: 'all' as const, label: 'All', count: notifications.length },
                { value: 'comments' as const, label: 'Comments', count: commentNotifications.length },
                { value: 'watching' as const, label: 'Following', count: watchedProjects.length },
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={cn(
                    "text-[11px] font-medium py-1 rounded-[20px] transition-all flex items-center gap-1",
                    activeTab === tab.value
                      ? "bg-primary text-primary-foreground px-2.5"
                      : "text-foreground/70 hover:text-foreground px-2.5"
                  )}
                  style={{ padding: '4px 10px', gap: '4px' }}
                >
                  {tab.label}
                  <span className={cn(
                    "text-[10px] tabular-nums",
                    activeTab === tab.value ? "text-primary-foreground/70" : "text-muted-foreground/40"
                  )}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <ScrollArea className="h-[400px]">
            <TabsContent value="all" className="m-0">
              {renderNotificationList(notifications)}
            </TabsContent>
            <TabsContent value="comments" className="m-0">
              {renderNotificationList(commentNotifications)}
            </TabsContent>
            <TabsContent value="watching" className="m-0">
              {renderWatchlist()}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
